import { AskAssistant, type AskAssistantError } from '@kilat/ui';
import { parseRateLimitError } from '@kilat/shared';

import { useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { callAskKilat } from '../lib/callables/ai.js';

/**
 * Dashboard "Ask Kilat" — thin wrapper around the shared `<AskAssistant>`
 * from `@kilat/ui` (extracted Sprint 74 right before customer-pwa
 * mounted its own variant; per [[feedback-unified-packages-ui]]).
 *
 * Owns:
 *   - `callAskKilat` callable wiring
 *   - Tenant id from custom claims
 *   - Operator-flavoured copy + suggestion chips
 *   - Pointer to the LLAMA_API_KEY runbook on NoOp answers
 *
 * Gated behind `Boolean(user)` so signed-out splash isn't cluttered.
 */
export function AskKilat() {
  const { claims, user } = useAuth();
  const { t } = useI18n();
  const brand = usePlatformBrand();
  if (!user) return null;
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  return (
    <AskAssistant
      launcherLabel={t('dashboard.askKilat.launcherLabel', { brand: brand.name })}
      headerTitle={t('dashboard.askKilat.headerTitle', { brand: brand.name })}
      headerSubtitle={t('dashboard.askKilat.headerSubtitle')}
      placeholder={t('dashboard.askKilat.placeholder')}
      emptyStateBody={t('dashboard.askKilat.emptyStateBody')}
      suggestionChips={[
        t('dashboard.askKilat.chip1'),
        t('dashboard.askKilat.chip2'),
        t('dashboard.askKilat.chip3'),
        t('dashboard.askKilat.chip4'),
      ]}
      noopExplainer={t('dashboard.askKilat.noopExplainer')}
      storageKey="kilat.askKilat.history"
      launcherMode="inline"
      launcherCompact
      onAsk={async (question) => {
        const result = await callAskKilat({
          question,
          ...(tenantId ? { tenantId } : {}),
        });
        return { text: result.text, cacheHit: result.cacheHit, provider: result.provider };
      }}
      parseError={(err): AskAssistantError => {
        const rl = parseRateLimitError(err);
        if (rl) return { message: rl.message, retryAfterSec: rl.retryAfterSec };
        const e = err as { message?: string };
        const msg = e.message?.includes('unauthenticated')
          ? t('dashboard.askKilat.errSessionExpired')
          : t('dashboard.askKilat.errUnreachable');
        return { message: msg };
      }}
    />
  );
}
